import type { Router, RouteRecordRaw } from 'vue-router';

import { usePermissionStoreWithOut } from '/@/store/modules/permission';

import { PageEnum } from '/@/enums/pageEnum';
import { useUserStoreWithOut } from '/@/store/modules/user';

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
// import { TeamStatusEnum } from '/@/api/business/model/teamModel';

// import { RootRoute } from '/@/router/routes';

const LOGIN_PATH = PageEnum.BASE_LOGIN;
const RESET_PATH = PageEnum.RESET_PAGE;
const GOOGLE_REDIRECT = PageEnum.GOOGLE_REDIRECT;

// const ROOT_PATH = RootRoute.path;

const whitePathList: string[] = [
  LOGIN_PATH, RESET_PATH, GOOGLE_REDIRECT, '/owner/login', '/privacy', '/terms',
];

export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const permissionStore = usePermissionStoreWithOut();
  router.beforeEach(async (to, from, next) => {
    const token = userStore.getToken;

    // Whitelist can be directly entered
    if (whitePathList.includes(to.path)) {
      if (to.path === '/owner/login' && token) {
        try {
          await userStore.getUserInfoAction();
          if (userStore.hasPermission('platform:manage')) {
            next('/owner');
            return;
          }
        } catch {}
      }
      if (to.path === LOGIN_PATH && token) {
        const isSessionTimeout = userStore.getSessionTimeout;
        try {
          await userStore.afterLoginAction();
          if (!isSessionTimeout) {
            next((to.query?.redirect as string) || '/');
            return;
          }
        } catch {}
      }
      next();
      return;
    }

    // token does not exist
    if (!token) {
      // You can access without permission. You need to set the routing meta.ignoreAuth to true
      if (to.meta.ignoreAuth) {
        next();
        return;
      }
      // redirect login page
      const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
        path: to.path.startsWith('/owner') ? '/owner/login' : LOGIN_PATH,
        replace: true,
      };
      if (to.path) {
        redirectData.query = {
          ...redirectData.query,
          redirect: to.path,
        };
      }
      next(redirectData);
      return;
    }

    // if (!info?.team) {
    //   router.push(PageEnum.BASE_LOGIN);
    // }

    // Jump to the 404 page after processing the login
    if (
      from.path === LOGIN_PATH &&
      to.name === PAGE_NOT_FOUND_ROUTE.name &&
      to.fullPath !== PageEnum.BASE_HOME
    ) {
      next(PageEnum.BASE_HOME);
      return;
    }

    // get userinfo while last fetch time is empty
    if (userStore.getLastUpdateTime === 0) {
      try {
        await userStore.getUserInfoAction();
      } catch (err) {
        next();
        return;
      }
    }

    // The owner identity has its own workspace, separate from daily admin UI.
    if (userStore.hasPermission('platform:manage') && !to.path.startsWith('/owner')) {
      next({ path: '/owner', replace: true });
      return;
    }

    if (permissionStore.getIsDynamicAddedRoute) {
      next();
      return;
    }
    await permissionStore.changePermissionCode();
    const routes = await permissionStore.buildRoutesAction();

    routes.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw);
    });

    router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);

    permissionStore.setDynamicAddedRoute(true);
    if (to.name === PAGE_NOT_FOUND_ROUTE.name) {
      // 动态添加路由后，此处应当重定向到fullPath，否则会加载404页面内容
      next({ path: to.fullPath, replace: true, query: to.query });
    } else {
      const redirectPath = (from.query.redirect || to.path) as string;
      const redirect = decodeURIComponent(redirectPath);
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect };
      next(nextData);
    }
  });

  // ── meta.permission enforcement ─────────────────────────────────────
  // Routes with `meta.permission: 'foo:bar'` are gated against the
  // user's permission codes (loaded by /user/logged into userStore).
  // Without this guard the meta is purely cosmetic — sidebars hide it
  // but anyone who knows the URL can navigate in.
  // 数组支持(老板 2026-09-05 审计修复):permission 可传权限码数组,
  // 任一码命中即放行(如审核工作台 data:qc 或 data:audit 皆可进);
  // 字符串单码保持原行为。
  router.beforeEach((to, _from, next) => {
    const required = to.meta?.permission as string | string[] | undefined;
    if (!required || (Array.isArray(required) && required.length === 0)) { next(); return; }
    const codes: string[] = (userStore as any).permissions || [];
    const allowed = Array.isArray(required)
      ? required.some((code) => codes.includes(code))
      : codes.includes(required);
    if (allowed) { next(); return; }
    import('ant-design-vue').then(({ message }) => {
      message.warning('没有访问该页面的权限');
    });
    next({ path: '/', replace: true });
  });
}
